import { APP_ROUTES } from '@/constants/pages';
import { url } from '@/helpers';
import { getBreadcrumbsSchema } from '@/helpers/jsonld';
import { BaseSearchManagerOutput } from '@/hooks/search/useSearch.types';
import { getLocale } from '@/locale';
import { Helmet } from 'react-helmet';
import { useLocation, useRouteMatch } from 'react-router-dom';
import { getDealsSerpBreadcrumbs } from '../helpers';
import { DealsSearchPreviewResponse, DealsSearchResultsResponse } from '../types/dealSearch';
import { getDealsSearchResultsPageSchema, getSeoProps } from './DealsSearchSEO.helpers';

type DealsSearchSEOProps = BaseSearchManagerOutput & {
  preview: DealsSearchPreviewResponse | null;
  list: DealsSearchResultsResponse | null;
  isExtended: boolean;
};

const DealsSearchSEO = ({ preview, list, isExtended, ...searchManager }: DealsSearchSEOProps) => {
  const location = useLocation();
  const match = useRouteMatch();
  const isIndexable =
    match.path === APP_ROUTES.DEALS_SERP_PREVIEW ||
    match.path === APP_ROUTES.DEALS_SERP ||
    match.path === APP_ROUTES.SISTA_MINUTEN_SERP;
  const seo = getSeoProps(searchManager, location, isExtended);
  const ogLocale = getLocale().replace('-', '_');

  const breadcrumbs = getDealsSerpBreadcrumbs(searchManager.q, searchManager.location, searchManager.type).map(
    (item) => ({
      ...item,
      url: `${url.getBaseUrl()}${(item.url ?? '').slice(1)}`,
    }),
  );

  const searchResultsSchema = getDealsSearchResultsPageSchema(preview, list, searchManager.type);

  return (
    <Helmet encodeSpecialCharacters={true}>
      <title>{seo.title}</title>
      <meta name="description" content={seo.description} />
      <meta name="keywords" content={seo.keywords} />
      <link rel="canonical" href={seo.canonical} />
      <meta property="og:url" content={seo.url} />
      <meta property="og:title" content={seo.title} />
      <meta property="og:locale" content={ogLocale} />
      <meta property="og:image" content={seo.image} />
      <meta property="og:description" content={seo.description} />
      <meta property="og:site_name" content="Bokadirekt" />
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:site" content="@bokadirekt" />
      {(!isIndexable || searchManager.isIndexable === false) && <meta name="robots" content="noindex, follow" />}
      <script type="application/ld+json">{getBreadcrumbsSchema(breadcrumbs)}</script>
      {searchResultsSchema && <script type="application/ld+json">{searchResultsSchema}</script>}
    </Helmet>
  );
};

export default DealsSearchSEO;
